import { estQcmSimple } from './questionUtils';
import { estCleQcmUnique } from './modePassation';

/**
 * Adaptation AUTOMATIQUE d'une sélection au lancement « En classe (cartes) »
 * (chantier composition, décision prof 2026-09-02 : on fait, on dit ce qu'on
 * a fait, on laisse annuler — jamais de question posée au prof).
 *
 * Pour chaque exercice de la sélection :
 *   - QCM à réponse unique → part tel quel ;
 *   - exercice généré dont la carte d'exerciseur propose un format QCM à
 *     réponse unique → REMPLACÉ par une instance QCM fraîche de la même carte
 *     (même chemin que « Nouvelle version »), contexte pédagogique conservé ;
 *   - sinon (question du prof en saisie, tableau, exerciseur sans QCM) →
 *     LAISSÉ DE CÔTÉ : hors de cette séance, toujours dans le panier.
 *
 * Module pur : les accès aux générateurs sont injectés (testable sans le
 * registre). `exerciseurKey` d'un exercice généré = `${attenduId}_${catId}_${formatKey}`
 * (posé par generatedItems dans QCMDashboard).
 *
 * @returns {{ questions: Array, remplacements: Array<{origine, remplacant}>, laisses: Array }}
 *   `questions` = la liste à lancer, dans l'ordre d'origine.
 */
/**
 * La carte d'exerciseur d'un exercice généré et son format QCM à réponse
 * unique, ou null : question de banque, exerciseur inconnu, carte sans QCM.
 */
const versionQcmDe = (q, { hasGenerator, getCategories }) => {
    const attenduId = q?.attendu;
    const mapKey = q?._meta?.exerciseurKey;
    if (!attenduId || !mapKey || !hasGenerator(attenduId)) return null;
    const cat = (getCategories(attenduId) || []).find((c) => mapKey.startsWith(`${attenduId}_${c.id}_`));
    const fmt = cat?.formatsList?.find(estCleQcmUnique);
    return cat && fmt ? { attenduId, cat, fmt } : null;
};

/**
 * R17 (chantier composer-evaluation) : les exercices qui ne peuvent pas
 * devenir un QCM simple — ni QCM à réponse unique, ni d'une carte qui en
 * propose un (148 « Inverse », question de banque en saisie). Un seul suffit
 * à griser les cartes QR. Sans générer : l'adaptation, elle, tire au lancement.
 */
export const sansVersionQcm = (questions, generateurs) =>
    (questions || []).filter((q) => !estQcmSimple(q) && !versionQcmDe(q, generateurs));

export const adapterPourCartes = (questions, { hasGenerator, getCategories, generateExercice }) => {
    const resultat = [];
    const remplacements = [];
    const laisses = [];
    (questions || []).forEach((q) => {
        if (estQcmSimple(q)) { resultat.push(q); return; }
        const version = versionQcmDe(q, { hasGenerator, getCategories });
        if (version) {
            const { attenduId, cat, fmt } = version;
            try {
                const gen = generateExercice(attenduId, fmt.key);
                if (gen && estQcmSimple(gen)) {
                    const remplacant = {
                        ...gen,
                        attendu: gen.attendu ?? attenduId,
                        niveau: gen.niveau ?? q.niveau,
                        matiere: gen.matiere ?? q.matiere,
                        _tmpId: `${q._tmpId}_qcm`,
                        _meta: {
                            ...(gen._meta || {}),
                            exerciseurKey: `${attenduId}_${cat.id}_${fmt.key}`,
                            ...(q._meta?.descripteur ? { descripteur: q._meta.descripteur } : {}),
                            ...(q._meta?.attenduTitre ? { attenduTitre: q._meta.attenduTitre } : {}),
                            remplaceDe: q._tmpId,
                        },
                    };
                    remplacements.push({ origine: q, remplacant });
                    resultat.push(remplacant);
                    return;
                }
            } catch {
                // génération impossible : on laisse de côté, sans casser le lancement
            }
        }
        laisses.push(q);
    });
    return { questions: resultat, remplacements, laisses };
};
